import React, { createContext, useContext, useState, useEffect } from "react";
import {
	onAuthStateChanged,
	signInWithEmailAndPassword,
	signInWithPopup,
	createUserWithEmailAndPassword,
	signOut,
	sendPasswordResetEmail,
	GoogleAuthProvider,
} from "firebase/auth";
import {
	addDoc,
	collection,
	doc,
	setDoc,
	getDoc,
	getDocs,
	updateDoc,
	deleteDoc,
	query,
	orderBy,
	serverTimestamp,
	increment,
} from "firebase/firestore";
import { auth, db, storage } from "../firebase";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";

const FirebaseContext = createContext();

export const useFirebase = () => useContext(FirebaseContext);

export const FirebaseProvider = ({ children }) => {
	const [user, setUser] = useState(null);
	const [loading, setLoading] = useState(true);

	// Monitor authentication state
	useEffect(() => {
		const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
			setUser(currentUser);
			setLoading(false);
		});
		return unsubscribe;
	}, []);

	// --------------------
	// Auth methods
	// --------------------
	// Sign in function
	const login = (email, password) => {
		return signInWithEmailAndPassword(auth, email, password);
	};

	// Google Sign in function
	const loginGoogle = () => {
		return signInWithPopup(auth, new GoogleAuthProvider());
	};

	// Register function
	const register = (email, password) => {
		return createUserWithEmailAndPassword(auth, email, password);
	};

	// Sign out function
	const logout = () => {
		return signOut(auth);
	};

	// Reset Password function
	const resetPassword = (email) => {
		return sendPasswordResetEmail(auth, email);
	};

	const isAuthenticated = () => {
		return !!user;
	};

	// --------------------
	// Firestore methods
	// --------------------
	const createDocumentRef = async (collectionName) => {
		try {
			const docRef = doc(collection(db, collectionName));

			return docRef;
		} catch (error) {
			console.error("Error writing document: ", error);
			throw error;
		}
	};

	/**
	 * Write document to Firestore
	 * @param {*} collectionName - Firestore collection name.
	 * @param {*} data Firestore document data.
	 * @param {*} docId Firestore document ID.
	 * @returns Document ID
	 */
	const writeDocument = async (collectionName, data, docRef = null) => {
		try {
			if (docRef === null) {
				docRef = await addDoc(collection(db, collectionName), {
					...data,
					createdBy: user?.uid || null,
					createdAt: serverTimestamp(),
				});
			} else {
				await setDoc(docRef, {
					...data,
					createdBy: user?.uid || null,
					createdAt: serverTimestamp(),
				});
			}

			return docRef.id;
		} catch (error) {
			console.error("Error writing document: ", error);
			throw error;
		}
	};

	// Read a single document from Firestore
	const readDocument = async (collectionName, docId) => {
		try {
			const docRef = doc(db, collectionName, docId);
			const docSnap = await getDoc(docRef);

			if (docSnap.exists()) {
				return docSnap.data();
			} else {
				throw new Error("No such document!");
			}
		} catch (error) {
			console.error("Error reading document: ", error);
			throw error;
		}
	};

	// Read all documents from a Firestore collection
	const readCollection = async (collectionName, orderField = null) => {
		try {
			const querySnapshot =
				orderField ?
					await getDocs(query(collection(db, collectionName), orderBy(orderField)))
				:	await getDocs(collection(db, collectionName));

			return querySnapshot.docs.map((doc) => ({
				id: doc.id,
				...doc.data(),
			}));
		} catch (error) {
			console.error("Error reading collection: ", error);
			throw error;
		}
	};

	// Update a document in Firestore
	const updateDocument = async (collectionName, docId, data) => {
		try {
			const docRef = doc(db, collectionName, docId);
			await updateDoc(docRef, {
				...data,
				updatedBy: user?.uid || null,
				updatedAt: serverTimestamp(),
			});

			return docRef.id;
		} catch (error) {
			console.error("Error updating document: ", error);
			throw error;
		}
	};

	// Delete a document from Firestore
	const deleteDocument = async (collectionName, docId) => {
		try {
			const docRef = doc(db, collectionName, docId);
			await deleteDoc(docRef);
		} catch (error) {
			console.error("Error deleting document: ", error);
			throw error;
		}
	};

	const uploadFile = async (collectionName, fileName, file) => {
		try {
			const storageRef = ref(storage, `${collectionName}/${fileName}`);
			const snapshot = await uploadBytes(storageRef, file);

			return storageRef.fullPath;
		} catch (error) {
			console.error("Error uploading file: ", error);
			throw error;
		}
	};

	// --------------------
	// Storage methods
	// --------------------
	const downloadFile = async (filePath, img) => {
		try {
			const storageRef = ref(storage, filePath);

			await getDownloadURL(storageRef)
				.then((url) => {
					// This can be downloaded directly:
					// const xhr = new XMLHttpRequest();
					// xhr.responseType = "blob";
					// xhr.onload = (event) => {
					//   const blob = xhr.response;
					// };
					// xhr.open("GET", url);
					// xhr.send();

					// Or inserted into an <img> element
					// const img = document.getElementById("myimg");
					img.setAttribute("src", url);

					return url;
				})
				.catch((error) => {
					console.error("Error downloading file: ", error);
					// A full list of error codes is available at
					// https://firebase.google.com/docs/storage/web/handle-errors
					switch (error.code) {
						case "storage/object-not-found":
							// File doesn't exist
							break;
						case "storage/unauthorized":
							// User doesn't have permission to access the object
							break;
						case "storage/canceled":
							// User canceled the upload
							break;

						// ...

						case "storage/unknown":
							// Unknown error occurred, inspect the server response
							break;
					}
				});
		} catch (error) {
			console.error("Error uploading file: ", error);
			throw error;
		}
	};

	const deleteFile = async (filePath) => {
		try {
			const storageRef = ref(storage, filePath);

			deleteObject(storageRef)
				.then(() => {
					// File deleted successfully
				})
				.catch((error) => {
					// Uh-oh, an error occurred!
				});
		} catch (error) {
			console.error("Error deleting file: ", error);
			throw error;
		}
	};

	const value = {
		user,
		login,
		loginGoogle,
		register,
		logout,
		resetPassword,
		isAuthenticated,
		auth,
		createDocumentRef,
		writeDocument,
		readDocument,
		readCollection,
		updateDocument,
		deleteDocument,
		uploadFile,
		downloadFile,
		deleteFile,
	};

	return <FirebaseContext.Provider value={value}>{!loading && children}</FirebaseContext.Provider>;
};
